import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonInput,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { useMutation } from '@apollo/client/react';
import { Button } from '@/components/ui/Button';
import { PlayerCard } from '../components/PlayerCard';
import { PLACE_BID } from '../../../graphql/mutations/marketEntry';
import { PLACE_TRANSFER_OFFER } from '../../../graphql/mutations/transferOffer';
import type {
  PlaceBidMutation,
  PlaceBidMutationVariables,
  PlaceTransferOfferMutation,
  PlaceTransferOfferMutationVariables,
} from '../../../types/graphql-generated';
import { useState } from 'react';
import type { OfferTarget } from './offerTarget';
import { posthog } from '../../../lib/posthog';

export type { OfferTarget };
export { OfferTargetFactory } from './offerTarget';

interface PlaceBidModalProps {
  isOpen: boolean;
  target: OfferTarget;
  leagueId: string;
  releaseClause?: number;
  onClose: () => void;
}

export const PlaceBidModal = ({
  isOpen,
  target,
  leagueId,
  releaseClause,
  onClose,
}: PlaceBidModalProps) => {
  const [amount, setAmount] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);

  const player = target.getPlayer();
  const offerLabel = target.getOfferLabel();
  const offerLabelCapitalized = offerLabel.charAt(0).toUpperCase() + offerLabel.slice(1);
  const titleLabel = target.isEditing() ? 'Actualizar oferta' : 'Realizar oferta';
  const buttonLabel = target.isEditing() ? 'Actualizar oferta' : 'Ofrecer';

  const price = target.getPrice();
  const budget = target.getBudget();

  const handleWillPresent = () => {
    setAmount(target.getInitialAmount());
  };

  const [placeBid, { loading: bidLoading }] = useMutation<
    PlaceBidMutation,
    PlaceBidMutationVariables
  >(PLACE_BID);

  const [placeTransferOffer, { loading: offerLoading }] = useMutation<
    PlaceTransferOfferMutation,
    PlaceTransferOfferMutationVariables
  >(PLACE_TRANSFER_OFFER);

  const loading = bidLoading || offerLoading;

  const amountValue = parseInt(amount, 10);

  const budgetAfterOffer = budget !== undefined && !isNaN(amountValue)
    ? budget - amountValue
    : null;

  const handleSubmit = async () => {
    setErrors([]);

    if (isNaN(amountValue) || amountValue <= 0) {
      setErrors(['El importe debe ser un número mayor que 0']);
      return;
    }

    const validationErrors = target.validate(amountValue);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const mutationErrors = await target.execute(amountValue, leagueId, { placeBid, placeTransferOffer });
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors);
      } else {
        posthog.capture('offer_made', { is_free_agent: target.isFreeAgent() });
        handleClose();
      }
    } catch (error) {
      setErrors([`Error al realizar la ${offerLabel}`]);
      console.error('Error placing offer:', error);
    }
  };

  const handleClose = () => {
    setErrors([]);
    setAmount('');
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose} onWillPresent={handleWillPresent}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">{titleLabel}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="flex flex-col gap-4">
          {/* Player Info */}
          <div>
            <div className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
              Jugador
            </div>
            <PlayerCard player={player} />
          </div>

          {/* Reference section */}
          <div>
            <div className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
              {offerLabelCapitalized}
            </div>

            <div className="rounded-2xl border border-[#e8ebf1] bg-white px-4 shadow-[0_1px_2px_rgba(20,30,60,0.04)]">
              {releaseClause !== undefined && (
                <div className="flex items-center justify-between border-b border-[#eef1f6] py-3 last:border-b-0">
                  <span className="text-sm font-semibold text-slate-600">Cláusula de rescisión</span>
                  <span className="rounded-lg bg-amber-100 px-3 py-1.5 font-heading text-sm font-extrabold text-amber-900">
                    {releaseClause.toLocaleString('es-ES')}€
                  </span>
                </div>
              )}

              {price !== undefined ? (
                <div className="flex items-center justify-between border-b border-[#eef1f6] py-3 last:border-b-0">
                  <span className="text-sm font-semibold text-slate-600">Precio de venta</span>
                  <span className="rounded-lg bg-[#eef1f6] px-3 py-1.5 font-heading text-sm font-extrabold text-[var(--fc-ink)]">
                    {price.toLocaleString('es-ES')}€
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between border-b border-[#eef1f6] py-3 last:border-b-0">
                    <span className="text-sm font-semibold text-slate-600">Presupuesto</span>
                    <span className="rounded-lg bg-[#eef1f6] px-3 py-1.5 font-heading text-sm font-extrabold text-[var(--fc-ink)]">
                      {budget!.toLocaleString('es-ES')}€
                    </span>
                  </div>
                  {budgetAfterOffer !== null && amountValue > 0 && (
                    <div className="flex items-center justify-between border-b border-[#eef1f6] py-3 last:border-b-0">
                      <span className="text-sm font-semibold text-slate-600">Tras oferta</span>
                      <span
                        className={`rounded-lg px-3 py-1.5 font-heading text-sm font-extrabold ${
                          budgetAfterOffer < 0 ? 'bg-red-100 text-red-700' : 'bg-[#eef1f6] text-[var(--fc-ink)]'
                        }`}
                      >
                        {budgetAfterOffer.toLocaleString('es-ES')}€
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {price !== undefined && (
              <p className="mt-2 text-xs text-slate-500">
                {target.isFreeAgent()
                  ? `Agente libre: la ${offerLabel} debe ser igual o mayor al precio solicitado.`
                  : 'Puedes ofertar cualquier cantidad.'}
              </p>
            )}
          </div>

          {/* Amount Input */}
          <IonInput
            className="text-sm font-semibold"
            type="number"
            label={`Importe de la ${offerLabel} (€)`}
            labelPlacement="floating"
            fill="outline"
            value={amount}
            onIonInput={(e) => setAmount(e.detail.value || '')}
            placeholder="Introduce el importe"
            min="1"
            step="1"
            inputmode="numeric"
          />

          {/* Errors */}
          {errors.length > 0 && (
            <div>
              {errors.map((error, index) => (
                <IonText color="danger" key={index}>
                  <p className="text-sm">{error}</p>
                </IonText>
              ))}
            </div>
          )}

          {/* Info Text */}
          <p className="text-xs text-slate-500">
            {target.isEditing()
              ? `Puedes actualizar el importe de tu ${offerLabel} en cualquier momento.`
              : offerLabel === 'oferta directa'
                ? 'El manager recibirá una notificación y podrá aceptar o rechazar tu oferta.'
                : `Tu ${offerLabel} será visible para el vendedor.`}
          </p>

          {/* Submit Button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleSubmit}
            disabled={loading || !amount}
            className="mt-1"
          >
            {loading ? <IonSpinner name="crescent" /> : buttonLabel}
          </Button>
        </div>
      </IonContent>
    </IonModal>
  );
};
